// Flange sizes, shared by the pumps (src/library/pumps.ts) and the rubber joint on a pipe end (src/pipes/api.ts).
// No imports: the pipe engine and the library both read it without an import cycle.

export interface Flange {
  /** outside diameter, mm */
  od: number;
  /** thickness, mm */
  t: number;
  /** bolt circle (stud pattern) diameter, mm */
  pcd: number;
  /** number of bolt holes */
  n: number;
  /** bolt hole diameter, mm */
  hole: number;
}

/** Standard 16 bar (PN16) flange: outside diameter, thickness, bolt circle, holes, hole diameter. NOT from the catalogue. */
const FLANGES: Record<number, Flange> = {
  32: { od: 140, t: 18, pcd: 100, n: 4, hole: 18 },
  40: { od: 150, t: 18, pcd: 110, n: 4, hole: 18 },
  50: { od: 165, t: 18, pcd: 125, n: 4, hole: 18 },
  65: { od: 185, t: 18, pcd: 145, n: 4, hole: 18 },
  80: { od: 200, t: 20, pcd: 160, n: 8, hole: 18 },
  100: { od: 220, t: 20, pcd: 180, n: 8, hole: 18 },
  125: { od: 250, t: 22, pcd: 210, n: 8, hole: 18 },
  150: { od: 285, t: 22, pcd: 240, n: 8, hole: 22 },
  200: { od: 340, t: 24, pcd: 295, n: 12, hole: 22 },
  250: { od: 405, t: 26, pcd: 355, n: 12, hole: 26 },
  300: { od: 460, t: 28, pcd: 410, n: 12, hole: 26 }
};

/** The PN16 flange of size `dn`. Any size not in the table is scaled from the table rule. */
export function flangeOf(dn: number): Flange {
  const f = FLANGES[dn];
  if (f) return f;
  const od = Math.round(dn * 1.8 + 50);
  return { od, t: Math.round(14 + dn / 25), pcd: od - 40, n: dn > 150 ? 12 : dn > 65 ? 8 : 4, hole: 18 };
}

/**
 * Face to face length of a single sphere rubber expansion joint (PN16, flanged), mm. Typical values of the common
 * catalogues, not one maker's table. Any other size: 0.6 x DN + 80.
 */
const JOINT_LENGTH: Record<number, number> = { 32: 95, 40: 95, 50: 105, 65: 115, 80: 135, 100: 150, 125: 165, 150: 180, 200: 210, 250: 230, 300: 245 };
export const rubberJointLength = (dn: number): number => JOINT_LENGTH[dn] ?? Math.round(0.6 * dn + 80);

/**
 * AS 2129 Table E flanges (the "T/E" on Australian drawings, e.g. JagPoly lint pots): outside diameter, bolt circle,
 * number of holes and hole diameter of the standard. The thickness is a PVC/PE backing ring thickness, from the PN16 rule.
 */
const TABLE_E: Record<number, { od: number; pcd: number; n: number; hole: number }> = {
  32: { od: 120, pcd: 87, n: 4, hole: 18 },
  40: { od: 135, pcd: 98, n: 4, hole: 18 },
  50: { od: 150, pcd: 114, n: 4, hole: 18 },
  65: { od: 165, pcd: 127, n: 4, hole: 18 },
  80: { od: 185, pcd: 146, n: 4, hole: 18 },
  100: { od: 215, pcd: 178, n: 4, hole: 18 },
  125: { od: 255, pcd: 210, n: 8, hole: 18 },
  150: { od: 280, pcd: 235, n: 8, hole: 18 },
  200: { od: 335, pcd: 292, n: 8, hole: 18 },
  250: { od: 405, pcd: 356, n: 8, hole: 22 },
  300: { od: 455, pcd: 406, n: 12, hole: 22 }
};

/** The Table E flange of size `dn`; a size not in the table takes the PN16 rule. */
export function tableEFlange(dn: number): Flange {
  const e = TABLE_E[dn];
  return e ? { ...e, t: flangeOf(dn).t } : flangeOf(dn);
}
